import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Calendar, Users, Wrench, ExternalLink } from 'lucide-react';
import { Reveal } from './Reveal';
import { CoverMedia } from './CoverMedia';
import { UserFlow } from './UserFlow';
import { InfoArchitecture } from './InfoArchitecture';

const GRID_CHUNK_SIZE = 8;

const getSrc = (image) => (typeof image === 'string' ? image : image.src);
const getLabel = (image) => (typeof image === 'string' ? null : image.label);

const chunk = (arr, size) => {
  const groups = [];
  for (let i = 0; i < arr.length; i += size) groups.push(arr.slice(i, i + size));
  return groups;
};

export const CaseStudyHero = ({ caseStudy, onBack }) => (
  <section className="relative pt-32 pb-16 px-6 lg:px-12 overflow-hidden">
    <div className="absolute inset-0 onyx-mesh" />
    <div className="max-w-7xl mx-auto relative z-10">
      <button
        onClick={onBack}
        data-testid="back-home-button"
        className="glass-btn inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm text-zinc-300 mb-10"
      >
        <ArrowLeft size={16} /> Back to Home
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-xs text-zinc-500 uppercase tracking-[0.3em] mb-4">{caseStudy.company}</p>
          <h1 className="text-4xl md:text-6xl font-heading font-extrabold tracking-tight mb-4 silver-text">{caseStudy.title}</h1>
          <p className="text-xl text-zinc-300 mb-5 font-light">{caseStudy.subtitle}</p>
          <p className="text-base text-zinc-500 mb-8 font-light leading-relaxed">{caseStudy.overview}</p>
          <div className="flex flex-wrap gap-2">
            {caseStudy.services.map((service) => (
              <span key={service} className="text-xs font-medium px-3 py-1.5 rounded-full border border-white/10 text-zinc-400">{service}</span>
            ))}
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="glass rounded-3xl p-3 overflow-hidden"
        >
          <CoverMedia item={caseStudy} className="w-full rounded-2xl" />
        </motion.div>
      </div>
    </div>
  </section>
);

export const StickyNav = ({ sections, activeSection, onNavigate }) => (
  <div className="sticky top-20 z-40 glass-nav">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <nav className="flex overflow-x-auto py-4 gap-7 scrollbar-hide">
        {sections.map((section) => (
          <button
            key={section.id}
            data-testid={`cs-nav-${section.id}`}
            onClick={() => onNavigate(section.id)}
            className={`text-sm font-medium whitespace-nowrap transition-colors ${
              activeSection === section.id ? 'text-white' : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            {section.label}
          </button>
        ))}
      </nav>
    </div>
  </div>
);

export const Overview = ({ caseStudy }) => {
  const items = [
    { icon: Users, label: 'My Role', value: caseStudy.role },
    { icon: Calendar, label: 'Timeline', value: caseStudy.timeline },
    { icon: Users, label: 'Team', value: caseStudy.team },
    { icon: Wrench, label: 'Tools', value: caseStudy.tools.join(', ') },
  ];
  return (
    <section id="overview" className="mb-24 scroll-mt-40">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((item, idx) => (
          <Reveal key={item.label} delay={idx * 0.06}>
            <div className="glass glass-hover rounded-2xl p-6 h-full">
              <div className="flex items-center mb-3">
                <span className="w-10 h-10 rounded-xl glass-btn flex items-center justify-center mr-3">
                  <item.icon size={18} className="text-zinc-300" />
                </span>
                <span className="text-sm font-heading font-bold text-zinc-200 uppercase tracking-wider">{item.label}</span>
              </div>
              <p className="text-zinc-400 font-light leading-relaxed">{item.value}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

export const Challenge = ({ caseStudy }) => (
  <section id="problem" className="mb-24 scroll-mt-40">
    <Reveal>
      <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6 silver-text">The Challenge</h2>
      <p className="text-xl text-zinc-300 mb-6 font-light leading-relaxed">{caseStudy.problem.description}</p>
      <div className="glass rounded-2xl p-7 border-l-2 border-l-zinc-500">
        <h3 className="font-heading font-bold text-sm uppercase tracking-wider mb-4 text-zinc-300">Key Pain Points</h3>
        <ul className="space-y-3">
          {caseStudy.problem.painPoints.slice(0, 4).map((point) => (
            <li key={point} className="flex items-start text-zinc-400">
              <span className="text-zinc-600 mr-3 mt-1">—</span>
              <span className="font-light">{point}</span>
            </li>
          ))}
        </ul>
      </div>
    </Reveal>
  </section>
);

export const Insights = ({ caseStudy }) => (
  <section id="research" className="mb-24 scroll-mt-40">
    <Reveal>
      <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6 silver-text">Key Insights</h2>
    </Reveal>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {caseStudy.research.insights.slice(0, 4).map((insight, idx) => (
        <Reveal key={insight} delay={idx * 0.06}>
          <div className="glass glass-hover rounded-2xl p-6 flex items-start h-full">
            <span className="text-2xl font-heading font-extrabold silver-text mr-4">{String(idx + 1).padStart(2, '0')}</span>
            <p className="text-zinc-400 font-light leading-relaxed">{insight}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const Solution = ({ caseStudy }) => (
  <section id="solution" className="mb-24 scroll-mt-40">
    <Reveal>
      <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6 silver-text">The Solution</h2>
      <p className="text-lg text-zinc-400 mb-8 font-light leading-relaxed">{caseStudy.solution.approach}</p>
    </Reveal>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {caseStudy.solution.goals.slice(0, 4).map((goal) => (
        <Reveal key={goal} delay={0.06}>
          <div className="glass glass-hover rounded-2xl p-6 flex items-start h-full">
            <span className="text-zinc-300 text-lg mr-3 mt-0.5">✓</span>
            <p className="text-zinc-400 font-light leading-relaxed">{goal}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const Process = ({ caseStudy }) => {
  const { designProcess } = caseStudy;
  return (
    <section id="process" className="mb-24 scroll-mt-40">
      <Reveal>
        <h2 className="text-3xl md:text-4xl font-heading font-bold mb-8 silver-text">Design Process</h2>
      </Reveal>

      {designProcess.userFlowImage && (
        <Reveal className="mb-8">
          <h3 className="text-lg font-heading font-semibold mb-4 text-zinc-300">User Flow</h3>
          <div className="glass rounded-2xl p-4 overflow-hidden">
            <img src={designProcess.userFlowImage} alt={`${caseStudy.title} User Flow`} className="w-full rounded-xl bg-white" />
          </div>
        </Reveal>
      )}

      {designProcess.wireframeImages && (
        <Reveal className="mb-8">
          <h3 className="text-lg font-heading font-semibold mb-4 text-zinc-300">Wireframes</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {designProcess.wireframeImages.map((image) => (
              <div key={image} className="glass rounded-2xl p-4 overflow-hidden">
                <img src={image} alt={`${caseStudy.title} Wireframe`} className="w-full rounded-xl bg-white" />
              </div>
            ))}
          </div>
        </Reveal>
      )}

      {designProcess.prototype && (
        <Reveal className="mb-8">
          <h3 className="text-lg font-heading font-semibold mb-4 text-zinc-300">Interactive Prototype</h3>
          <a
            href={designProcess.prototype.link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="prototype-link"
            className="glass-btn inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium text-white"
          >
            <ExternalLink size={16} /> View in Figma
          </a>
        </Reveal>
      )}

      {designProcess.informationArchitecture && (
        <Reveal className="mb-12">
          <h3 className="text-lg font-heading font-semibold mb-5 text-zinc-300">Information Architecture</h3>
          <InfoArchitecture ia={designProcess.informationArchitecture} />
        </Reveal>
      )}

      {designProcess.userFlow && (
        <Reveal>
          <h3 className="text-lg font-heading font-semibold mb-5 text-zinc-300">User Flow</h3>
          <UserFlow flow={designProcess.userFlow} />
        </Reveal>
      )}
    </section>
  );
};

const GridScreen = ({ image, index }) => {
  const src = getSrc(image);
  const label = getLabel(image);
  return (
    <div className="group glass glass-hover rounded-2xl p-2.5 h-full flex flex-col">
      <div className="overflow-hidden rounded-xl bg-[#0A0A0C]">
        <img src={src} alt={label || `design ${index + 1}`} className="w-full h-auto rounded-xl group-hover:scale-[1.03] transition-transform duration-500" />
      </div>
      {label && (
        <p className="mt-3 px-1 pb-1 text-xs font-medium text-zinc-400 flex items-start gap-2">
          <span className="font-mono text-zinc-600">{String(index + 1).padStart(2, '0')}</span>
          {label}
        </p>
      )}
    </div>
  );
};

export const FinalDesign = ({ caseStudy }) => {
  const { images, layout } = caseStudy.uiDesign;
  return (
    <section id="design" className="mb-24 scroll-mt-40">
      <Reveal>
        <h2 className="text-3xl md:text-4xl font-heading font-bold mb-8 silver-text">Final Design</h2>
      </Reveal>
      {layout === 'grid' ? (
        <div className="space-y-8">
          {chunk(images, GRID_CHUNK_SIZE).map((group, ci) => (
            <div key={getSrc(group[0])} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {group.map((image, j) => (
                <Reveal key={getSrc(image)} delay={j * 0.04}>
                  <GridScreen image={image} index={ci * GRID_CHUNK_SIZE + j} />
                </Reveal>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-10">
          {images.map((image, idx) => {
            const src = getSrc(image);
            const label = getLabel(image);
            return (
              <Reveal key={src} delay={idx * 0.05}>
                {label && (
                  <h3 className="text-lg font-heading font-semibold mb-4 text-zinc-300 flex items-center gap-3">
                    <span className="text-sm font-mono text-zinc-600">{String(idx + 1).padStart(2, '0')}</span>
                    {label}
                  </h3>
                )}
                <div className="glass rounded-3xl p-3 overflow-hidden">
                  <img src={src} alt={`${caseStudy.title} design ${idx + 1}`} className="w-full rounded-2xl" />
                </div>
              </Reveal>
            );
          })}
        </div>
      )}
    </section>
  );
};

export const Impact = ({ caseStudy }) => (
  <section id="outcome" className="mb-24 scroll-mt-40">
    <Reveal>
      <h2 className="text-3xl md:text-4xl font-heading font-bold mb-8 silver-text">Impact</h2>
    </Reveal>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {caseStudy.outcome.impact.slice(0, 4).map((item) => (
        <Reveal key={item} delay={0.06}>
          <div className="glass glass-hover rounded-2xl p-6 h-full">
            <p className="text-zinc-300 font-light leading-relaxed">{item}</p>
          </div>
        </Reveal>
      ))}
    </div>
    {caseStudy.outcome.feedback && (
      <Reveal delay={0.1}>
        <blockquote className="glass rounded-3xl p-8 md:p-10 mt-6 border-l-2 border-l-zinc-400">
          <p className="text-xl md:text-2xl font-heading font-medium text-zinc-100 leading-snug">
            &ldquo;{caseStudy.outcome.feedback}&rdquo;
          </p>
          <footer className="mt-4 text-xs text-zinc-500 uppercase tracking-[0.2em]">{caseStudy.company} — Stakeholder Feedback</footer>
        </blockquote>
      </Reveal>
    )}
  </section>
);

export const Learnings = ({ caseStudy }) => (
  <section id="learnings" className="mb-8 scroll-mt-40">
    <Reveal>
      <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6 silver-text">Key Learnings</h2>
    </Reveal>
    <div className="space-y-4">
      {caseStudy.learnings.slice(0, 3).map((learning) => (
        <Reveal key={learning} delay={0.06}>
          <div className="glass rounded-2xl p-6 border-l-2 border-l-zinc-500">
            <p className="text-zinc-400 font-light leading-relaxed">{learning}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const NextProject = ({ nextProject }) => (
  <section className="py-20 px-6 lg:px-12 border-t border-white/5">
    <div className="max-w-7xl mx-auto">
      <h2 className="text-2xl md:text-3xl font-heading font-bold mb-8 text-zinc-300">Next Project</h2>
      <Link to={`/case-study/${nextProject.id}`} data-testid="next-project-link" className="group block glass glass-hover rounded-3xl overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="aspect-[16/10] lg:aspect-auto overflow-hidden bg-[#0A0A0C]">
            <CoverMedia item={nextProject} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 brightness-90 group-hover:brightness-100" />
          </div>
          <div className="p-9 flex flex-col justify-center">
            <p className="text-xs text-zinc-500 uppercase tracking-[0.2em] mb-3">{nextProject.company}</p>
            <h3 className="text-3xl font-heading font-bold mb-3 text-white group-hover:silver-text transition-all">{nextProject.title}</h3>
            <p className="text-zinc-400 mb-6 font-light">{nextProject.description}</p>
            <span className="inline-flex items-center gap-2 text-sm font-medium text-zinc-300 group-hover:gap-3 transition-all">
              View Case Study <ArrowRight size={18} />
            </span>
          </div>
        </div>
      </Link>
    </div>
  </section>
);
